import { useState } from 'react';
import ImageGenerator from '../components/ImageGenerator';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HomePage = () => {
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateImage = async (prompt: string) => {
    // If prompt is empty, just clear the generated image
    if (!prompt.trim()) {
      setGeneratedImage(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedImage(null);
    
    try {
      // Use local worker endpoint during development, production endpoint in production
      const apiUrl = import.meta.env.DEV 
        ? 'http://localhost:8787/generate'
        : `${import.meta.env.VITE_WORKER_URL}/generate`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (errorData && errorData.error) {
          throw new Error(errorData.error + (errorData.details ? `: ${errorData.details}` : ''));
        } else {
          throw new Error(`Failed to generate image: ${response.status} ${response.statusText}`);
        }
      }

      // Check if the response is JSON (error) or image (success)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error + (errorData.details ? `: ${errorData.details}` : ''));
      }
      
      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      setGeneratedImage(imageUrl);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(`${errorMessage}. Please try again later.`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <ImageGenerator 
          onGenerate={handleGenerateImage} 
          generatedImage={generatedImage}
          isLoading={isLoading}
          error={error}
        />
      </main>
      
      <Footer />
    </div>
  );
};

export default HomePage; 